import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import useDebounce from "../hook/useDebounce";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { TextWrap } from "./ui/TextWrap";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const UserPermissions = () => {
  const [userPermissions, setUserPermissions] = useState([]);
  const [filteredUserPermissions, setFilteredUserPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const debouncedSearchQuery = useDebounce(searchTerm, 500);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedFetchPermissions = useCallback(
    debounce((page, perPage, search, sortBy, sortOrder) => {
      fetchUserPermissions(page, perPage, search, sortBy, sortOrder);
    }, 500),
    []
  );

  const fetchUserPermissions = async (
    page = 1,
    perPage = entriesPerPage,
    search = "",
    sortBy = null,
    sortOrder = "asc"
  ) => {
    try {
      setLoading(true);
      setErrorMessage("");
      const queryParams = new URLSearchParams({
        page,
        per_page: perPage,
        search,
        sort_by: sortBy || "",
        sort_order: sortOrder,
      });

      const response = await apiCall(
        "get",
        `/get-all-user-permissions?${queryParams.toString()}`
      );

      if (response?.data?.success) {
        const { data, total, pages } = response.data;
        const formattedData = (data || []).map((permission) => ({
          ...permission,
          display_created_datetime: permission.created_datetime
            ? new Date(permission.created_datetime).toLocaleDateString(
                "en-GB",
                {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                }
              )
            : "-",
        }));
        setUserPermissions(formattedData);
        setFilteredUserPermissions(formattedData);
        setTotalEntries(total || formattedData.length);
        setTotalPages(pages || Math.ceil(total / perPage));
        setCurrentPage(page);

        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "GET");
      } else {
        setErrorMessage("Failed to load user permissions.");
        handleCustomResponse("error", "Failed to load user permissions.");
      }
    } catch (error) {
      setErrorMessage(error.message || "Error fetching user permissions.");
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
    } finally {
      setLoading(false);
    }
  };

  const deleteUserPermissions = async () => {
    try {
      setDeleteLoading(true);
      const response = await apiCall(
        "delete",
        `/delete-user-permission/${deleteId}`
      );

      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "DELETE");
        fetchUserPermissions(
          currentPage,
          entriesPerPage,
          searchTerm,
          sortConfig.field,
          sortConfig.direction
        );
        setShowDeleteModal(false);
        setDeleteId(null);
      } else {
        handleCustomResponse("error", "Failed to delete permissions.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "DELETE");
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPermissions(
      currentPage,
      entriesPerPage,
      debouncedSearchQuery,
      sortConfig.field,
      sortConfig.direction
    );
  }, [
    entriesPerPage,
    debouncedSearchQuery,
    sortConfig.field,
    sortConfig.direction,
  ]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    debouncedFetchPermissions(
      1,
      entriesPerPage,
      value,
      sortConfig.field,
      sortConfig.direction
    );
  };

  const handleEntriesPerPageChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
    fetchUserPermissions(
      1,
      value,
      searchTerm,
      sortConfig.field,
      sortConfig.direction
    );
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchUserPermissions(
        page,
        entriesPerPage,
        searchTerm,
        sortConfig.field,
        sortConfig.direction
      );
    }
  };

  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  const sortedPermissions = [...filteredUserPermissions].sort((a, b) => {
    if (!sortConfig.field) return 0;
    const valueA = a[sortConfig.field] || "";
    const valueB = b[sortConfig.field] || "";
    if (sortConfig.field === "is_active") {
      const valA = valueA ? "Allowed" : "Not Allowed";
      const valB = valueB ? "Allowed" : "Not Allowed";
      return sortConfig.direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    if (sortConfig.field === "created_datetime") {
      const dateA = valueA ? new Date(valueA).getTime() : 0;
      const dateB = valueB ? new Date(valueB).getTime() : 0;
      return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
    }
    return sortConfig.direction === "asc"
      ? valueA.toString().localeCompare(valueB.toString())
      : valueB.toString().localeCompare(valueA.toString());
  });

  const startEntry =
    totalEntries > 0 ? (currentPage - 1) * entriesPerPage + 1 : 0;
  const endEntry = Math.min(currentPage * entriesPerPage, totalEntries);
  if (errorMessage) {
    return <ErrorAlert />;
  }
  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by permission key..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={entriesPerPage}
              handlePerPageChange={handleEntriesPerPageChange}
            />
            <div className="ms-auto">
              {hasPermission("assign_user_permission") && (
                <button
                  className="btn btn-primary-600 btn-sm d-flex align-items-center"
                  onClick={() => navigate("/assign-permissions")}
                >
                  <Icon icon="ri:add-line" className="me-1" />
                  Assign Permissions
                </button>
              )}
            </div>
          </div>

          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          {sortedPermissions.length === 0 && !loading ? (
            <TableDataNoAvailable message="No User permissions found." />
          ) : (
            <div className="position-relative">
              <div
                className="table-responsive"
                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <table className="table bordered-table mb-0 table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th scope="col" className="p-2">
                        S.No
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("permission_key")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Permission Key</span>
                          <SortIcons
                            field="permission_key"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("module_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Module Name</span>
                          <SortIcons
                            field="module_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("description")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Description</span>
                          <SortIcons
                            field="description"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("is_active")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex ">
                          <span>Status</span>
                          <SortIcons
                            field="is_active"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("created_by")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created By</span>
                          <SortIcons
                            field="created_by"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("created_datetime")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created Date</span>
                          <SortIcons
                            field="created_datetime"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th scope="col" className="p-2">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPermissions.map((permission, index) => (
                      <tr key={`${permission.permission_id}-${index}`}>
                        <td className="p-2">{startEntry + index}</td>
                        <td className="p-2">
                          {permission.permission_key || "-"}
                        </td>
                        <td className="p-2">
                          {permission.module_name || "-"}
                        </td>
                        <td className="p-2">
                          <TextWrap
                            location={permission.description || "-"}
                          />
                        </td>
                        <td className="p-2 ">
                          <span
                            className={`px-3 py-1 radius-4 fw-medium text-sm ${
                              permission.is_active
                                ? "bg-success-focus text-success-600 border border-success-main"
                                : "bg-danger-focus text-danger-600 border border-danger-main"
                            }`}
                          >
                            {permission.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="p-2">
                          {permission.created_by || "-"}
                        </td>
                        <td className="p-2">
                          {permission.display_created_datetime}
                        </td>
                        <td className="p-2">
                          {hasPermission("delete_user_permission") && (
                            <button
                              className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{
                                position: "relative",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";
                                const tooltip = document.createElement("div");
                                tooltip.textContent = "Delete";
                                tooltip.style.cssText = `
                                  position: absolute;
                                  top: -35px;
                                  left: 50%;
                                  transform: translateX(-50%);
                                  background-color: #333;
                                  color: white;
                                  padding: 5px 8px;
                                  border-radius: 4px;
                                  font-size: 12px;
                                  white-space: nowrap;
                                  z-index: 1000;
                                  pointer-events: none;
                                `;
                                tooltip.id = "edit-tooltip";
                                e.currentTarget.appendChild(tooltip);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                const tooltip =
                                  document.getElementById("edit-tooltip");
                                if (tooltip) tooltip.remove();
                              }}
                              title="Delete"
                              onClick={() => {
                                setDeleteId(permission.permission_id);
                                setShowDeleteModal(true);
                              }}
                            >
                              <Icon icon="mingcute:delete-2-line" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}
              <PaginationControls
                pagination={{
                  currentPage,
                  perPage: entriesPerPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Permission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the permission with ID {deleteId}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={deleteUserPermissions}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Please wait..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserPermissions;
